import { useCallback } from 'react';
import { useApolloClient, useMutation } from 'react-apollo';
import { useDispatch, useSelector } from 'react-redux';

import {
  accountLoginByPhone as accountLoginByPhoneMutation,
  phoneVerificationRequest as phoneVerificationRequestMutation,
} from '../../../graphql/mutations/account';
import {
  phoneAuthSubmit,
  phoneAuthVerify,
  resendPhoneAuthCode,
} from '../../../actions/phone-auth';
import { PHONE_VERIFY_INTENT } from '../../../constants/auth';
import { gtm } from '../../../util/analytics';
import { MeQuery } from '../../../graphql/queries/account';

const usePhoneAuth = () => {
  const client = useApolloClient();
  const dispatch = useDispatch();
  const [
    loading,
    error,
    pinSent,
    requireHumanInput,
    blockLogin,
  ] = useSelector(({ auth }) => [
    auth.loading,
    auth.error,
    auth.pinSent,
    auth.requireHumanInput,
    auth.blockLogin,
  ]);

  const [phoneVerificationRequest] = useMutation(
    phoneVerificationRequestMutation,
  );

  const onPhoneVerificationRequest = async ({
    phoneNumber,
    captchaToken = undefined,
    forceAllowAction = false,
  }) => {
    const result = await phoneVerificationRequest({
      variables: {
        input: {
          platform: 'WEB',
          phone_number: phoneNumber,
          captcha_token: captchaToken,
          force_allow_action: forceAllowAction,
          intent: PHONE_VERIFY_INTENT.LOGIN,
        },
      },
    });

    return result;
  };

  const [accountLoginByPhone] = useMutation(accountLoginByPhoneMutation);

  const onLoginByPhone = async ({ phoneNumber, code }) => {
    const result = await accountLoginByPhone({
      variables: {
        input: {
          phone_number: phoneNumber,
          phone_verification_code: code,
          set_cookie: true,
        },
      },
    });

    const {
      data: { accountLoginByPhone: accountLoginByPhoneRes } = {},
    } = result;

    if (accountLoginByPhoneRes && !accountLoginByPhoneRes?.existing_account) {
      const { data: { me } = {} } = await client.query({ query: MeQuery });

      if (me?.id) {
        gtm.trackCustomEvent({
          event: 'Consumer_Account_Created',
          user_id: me?.id,
        });
      }
    }

    return result;
  };

  const onPhoneAuthSubmit = useCallback(
    (phone, rethrow, captchaToken = undefined, forceAllowAction = false) =>
      dispatch(
        phoneAuthSubmit(
          onPhoneVerificationRequest,
          phone,
          rethrow,
          captchaToken,
          forceAllowAction,
        ),
      ),
    [dispatch],
  );

  const onPhoneAuthVerify = useCallback(
    (phone, code, onLogin) =>
      dispatch(phoneAuthVerify(onLoginByPhone, phone, code, onLogin)),
    [dispatch],
  );

  const onResendPhoneAuthCode = useCallback(
    (
      phone,
      rethrow = true,
      captchaToken = undefined,
      forceAllowAction = false,
    ) =>
      dispatch(
        resendPhoneAuthCode(
          onPhoneVerificationRequest,
          phone,
          rethrow,
          captchaToken,
          forceAllowAction,
        ),
      ),
    [dispatch],
  );

  return {
    loading,
    error,
    pinSent,
    requireHumanInput,
    blockLogin,
    onPhoneAuthSubmit,
    onPhoneAuthVerify,
    onResendPhoneAuthCode,
  };
};

export default usePhoneAuth;
